import { observable, component } from 'knockout-decorators'

import { IdentityProvider, LoginStatus } from '@tixa/schema'
import { ErrorReporter, Language } from '~/utils'
import { pageState } from '~/store/page'

import { isLoggedIntoFb, loadFb } from '../utils/fb'
import type { SignInOptions } from '../utils/options'
import { authState } from '../utils/state'

import source from './sign-in.html?raw'

@component('sign-in-form', source)
export class SignInForm {
  constructor(private options: SignInOptions) {
    this.initFbSdk().then((value) => {
      this.fbAvailable = value
    })
  }

  private state = authState

  @observable public email = authState.email
  @observable public password = ''

  @observable public fbAvailable = false
  @observable public loginIdp: IdentityProvider | undefined = undefined
  @observable public isFeedbackVisible = false

  @observable public isLoading = false

  private Locale = Language.getInstance()

  public i18n(key: string): string {
    return this.Locale.translations[key]
  }

  private async passAuthLogin() {
    if (this.isLoading || this.state.isLoading) return
    this.isFeedbackVisible = false
    this.state.isLoading = true
    this.isLoading = true

    const loginResponse = this.options.useJwt
      ? await pageState.api.authentication_jwt.login({
          email: this.email,
          password: this.password,
        })
      : await pageState.api.authentication.login({
          email: this.email,
          password: this.password,
        })

    if (loginResponse?.status === LoginStatus.success) {
      this.state.user = await pageState.api.user.GetUser()
      this.options.signedIn(this.email)
    } else {
      this.isFeedbackVisible = true
    }
    this.isLoading = false
    this.state.isLoading = false
  }

  private async tokenAuthLogin() {
    if (this.isLoading || this.state.isLoading) return
    this.isFeedbackVisible = false
    this.state.isLoading = true
    this.isLoading = true

    const loginResponse = await pageState.api.authentication.login({
      provider: this.loginIdp,
      email: this.email,
      token: this.state.token,
    })
    if (loginResponse.status === LoginStatus.success) {
      // assuming server acknowledged login, redirect
      this.state.user = await pageState.api.user.GetUser()
      this.options.signedIn(this.email)
    } else if (loginResponse.status === LoginStatus.unlinked) {
      // user already has account and haven't used idp before
      this.options.linkAccounts(this.email)
    }
    this.isLoading = false
    this.state.isLoading = false
  }

  // Facebook login
  private async initFbSdk(): Promise<boolean> {
    try {
      if (pageState.config.fbAppId) {
        return await loadFb(
          pageState.config.fbAppId,
          Language.getInstance().language,
          () => this.fbButtonLoginCallback()
        )
      }
      return false
    } catch (error) {
      ErrorReporter.exception(error as Error)
      return false
    }
  }

  private async fbButtonLoginCallback() {
    // keep
    if (await isLoggedIntoFb()) {
      FB.api('/me', { fields: 'email' }, (meResponse: any) => {
        // get authorization from internal source
        this.loginIdp = IdentityProvider.facebook
        this.email = meResponse.email
        this.tokenAuthLogin()
      })
    }
  }

  private gotoRegister() {
    if (this.isLoading || this.state.isLoading) return
    this.options.registerAccount(this.email)
  }

  private gotoLostpass() {
    if (this.isLoading || this.state.isLoading) return
    this.options.lostPassword(this.email)
  }
}
